/**
 * Helper utility to extract descriptive error messages from CKYC API responses and Axios errors.
 * Prioritizes long, detailed backend messages (e.g., CERSAI account balance errors) over generic status phrases like 'Bad Request'.
 */

export const isGenericErrorMessage = (str) => {
    if (typeof str !== "string") return true;
    const s = str.trim().toLowerCase();
    return (
        !s ||
        s === "bad request" ||
        s === "internal server error" ||
        s === "not found" ||
        s === "unauthorized" ||
        s === "forbidden" ||
        s === "conflict" ||
        s === "error" ||
        s === "failed" ||
        s === "failure" ||
        s.startsWith("request failed with status code")
    );
};

export const extractErrorMessage = (errOrRes, fallback = "An error occurred") => {
    if (!errOrRes) return fallback;
    let data = errOrRes?.response?.data || errOrRes;

    // Handle JSON string payload
    if (typeof data === "string" && data.trim()) {
        try {
            const parsed = JSON.parse(data);
            if (parsed && typeof parsed === "object") {
                data = parsed;
            }
        } catch (e) {
            // Raw text error that is not JSON
            if (!isGenericErrorMessage(data)) {
                return data.trim();
            }
        }
    }

    // 1. Array of validation messages (e.g., NestJS class-validator errors)
    if (Array.isArray(data?.message) && data.message.length > 0) {
        const nonGeneric = data.message.filter(m => typeof m === "string" && !isGenericErrorMessage(m));
        if (nonGeneric.length > 0) return nonGeneric.join(", ");
        return data.message.join(", ");
    }
    if (Array.isArray(data?.issues) && data.issues.length > 0) {
        const issueMsgs = data.issues
            .map((it) => (typeof it === "string" ? it : it?.message || it?.error || ""))
            .filter((m) => typeof m === "string" && !isGenericErrorMessage(m));
        if (issueMsgs.length > 0) return issueMsgs.join(", ");
    }

    // 2. Descriptive, non-generic error message candidates
    // Priority 1: message (descriptive backend message, e.g. "Insufficient account balance...")
    if (typeof data?.message === "string" && !isGenericErrorMessage(data.message)) {
        return data.message.trim();
    }
    // Priority 2: issues.message
    if (typeof data?.issues?.message === "string" && !isGenericErrorMessage(data.issues.message)) {
        return data.issues.message.trim();
    }
    // Priority 3: msg
    if (typeof data?.msg === "string" && !isGenericErrorMessage(data.msg)) {
        return data.msg.trim();
    }
    // Priority 4: error.message
    if (typeof data?.error?.message === "string" && !isGenericErrorMessage(data.error.message)) {
        return data.error.message.trim();
    }
    // Priority 5: issues.error
    if (typeof data?.issues?.error === "string" && !isGenericErrorMessage(data.issues.error)) {
        return data.issues.error.trim();
    }
    // Priority 6: error string (only if not generic like 'Bad Request')
    if (typeof data?.error === "string" && !isGenericErrorMessage(data.error)) {
        return data.error.trim();
    }
    // Priority 7: raw string data
    if (typeof data === "string" && !isGenericErrorMessage(data)) {
        return data.trim();
    }

    // 3. Fallback candidates if only generic strings are provided
    if (typeof data?.message === "string" && data.message.trim()) {
        return data.message.trim();
    }
    if (typeof data?.msg === "string" && data.msg.trim()) {
        return data.msg.trim();
    }
    if (typeof data?.issues?.message === "string" && data.issues.message.trim()) {
        return data.issues.message.trim();
    }
    if (typeof data?.issues?.error === "string" && data.issues.error.trim()) {
        return data.issues.error.trim();
    }
    if (typeof data?.error === "string" && data.error.trim()) {
        return data.error.trim();
    }
    if (typeof errOrRes?.message === "string" && !isGenericErrorMessage(errOrRes.message)) {
        return errOrRes.message.trim();
    }
    if (typeof errOrRes?.message === "string" && errOrRes.message.trim()) {
        return errOrRes.message.trim();
    }

    return fallback;
};
